import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  Heading,
  Divider,
  Badge,
  Button,
  SimpleGrid,
  useToast,
  Textarea,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import BreadCrumbMaker from "../../components/BreadCrumbMaker";
import { usePaymentStatusPolling } from "../../hooks/usePaymentStatusPolling";

export default function BookingStatus() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState("PENDING");
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [manualPaymentComment, setManualPaymentComment] = useState("");

  // Safely get values from location.state
  const {
    leadId, // Human-readable leadID for display
    leadMongoId, // MongoDB _id for API calls
    amount: rawAmount,
    bookingData,
    customerData,
    passengers,
    paymentId,
    tokenAmount: rawTokenAmount,
  } = location.state || {};

  // console.log("BookingStatus received location.state:", location.state);

  // Also accept systemCalculated total if passed explicitly from Booking page
  const {
    systemCalculated: rawSystemCalculated,
    negotiatedTotal: rawNegotiated,
  } = location.state || {};

  // -----------------------------
  // FIX: Convert amount safely
  // -----------------------------
  const amount = rawAmount ? Number(rawAmount) : 0;
  const tokenAmount = rawTokenAmount ? Number(rawTokenAmount) : 0;

  // Prefer negotiated total passed from Booking, then systemCalculated, then bookingData.fareDetails, then amount
  const negotiated =
    rawNegotiated !== undefined ? Number(rawNegotiated) : undefined;
  const systemCalculated =
    rawSystemCalculated !== undefined ? Number(rawSystemCalculated) : undefined;

  const totalAmount =
    Number(
      negotiated ??
      systemCalculated ??
      bookingData?.fareDetails?.systemCalculated ??
      bookingData?.fareDetails?.total ??
      bookingData?.fare ??
      amount,
    ) || 0;

  const normalizedTripType = String(bookingData?.tripType || "").toUpperCase();
  const isLocalRental = ["LR", "HR", "LOCAL", "LOCALRENTAL", "LOCAL_RENTAL"].includes(
    normalizedTripType,
  );
  const dropValue = (bookingData?.to || "").toString().trim().toLowerCase();
  const shouldShowDropLocation = !isLocalRental && dropValue !== "local";

  // console.log("Computed totalAmount:", totalAmount);

  if (!bookingData || !customerData || !leadId || !paymentId) {
    return (
      <Box bg="#F3F6FD" minH="100vh" p={4}>
        <Text color="red.500">Booking data not found. Please go back.</Text>
      </Box>
    );
  }

  const formatTime12 = (timeStr) => {
    if (!timeStr) return "";
    let [hours, minutes] = timeStr.split(":").map(Number);
    const modifier = hours >= 12 ? "PM" : "AM";
    if (hours > 12) hours -= 12;
    if (hours === 0) hours = 12;
    return `${hours}:${minutes.toString().padStart(2, "0")} ${modifier}`;
  };

  useEffect(() => {
    console.log("Payment ID received:", paymentId);
    console.log("Amount received:", tokenAmount, typeof tokenAmount);
  }, [paymentId, tokenAmount]);

  // AUTO-POLL PAYMENT STATUS
  usePaymentStatusPolling({
    paymentLinkIds: paymentId,
    enabled: paymentStatus === "PENDING" && !!paymentId,
    onPaymentDetected: async () => {
      try {
        const token = localStorage.getItem("prima_session");

        // Fetch updated payment/booking details
        const res = await axios.get(
          `${import.meta.env.VITE_API_URL}/payments/status/${paymentId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const bookingID = res.data?.bookingId || null;

        toast({
          title: "Payment Received!",
          description: "Token payment has been successfully processed.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        setPaymentStatus("PAID");

        // Navigate to booking confirmed page
        navigate("/bookings/lead/booking-confirmed", {
          state: {
            leadId,
            bookingId: bookingID,
            amount: totalAmount,
            systemCalculated,
            tokenAmount,
            bookingData,
            customerData,
            transactionID: res.data?.transactionID || `TXN${Date.now()}`,
          },
        });
      } catch (error) {
        console.error("Error handling payment detection:", error);
        toast({
          title: "Payment Received",
          description: "Please refresh to see updated status.",
          status: "info",
          duration: 4000,
          isClosable: true,
        });
        setPaymentStatus("PAID");
      }
    },
  });

  // MARK AS PAID manually
  const handleMarkAsPaid = async () => {
    setLoading(true);

    try {
      const token = localStorage.getItem("prima_session");

      if (!token) {
        toast({
          title: "Authentication Error",
          description: "Session token not found. Please login again.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setLoading(false);
        return;
      }

      const transactionID = `TXN${Date.now()}`;

      // FIX: Validate required fields
      if (!paymentId) {
        toast({
          title: "Error",
          description: "Payment ID is missing.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setLoading(false);
        return;
      }

      if (!leadMongoId) {
        toast({
          title: "Error",
          description: "Lead ID is missing.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setLoading(false);
        return;
      }

      if (!totalAmount || isNaN(totalAmount) || totalAmount <= 0) {
        toast({
          title: "Invalid Amount",
          description: "Payment amount is missing or invalid.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setLoading(false);
        return;
      }

      console.log("Marking payment as paid:", {
        paymentId,
        leadId: leadMongoId,
        amount: totalAmount,
        token,
        tokenAmount,
        transactionID,
      });

      let res;
      // If a receipt file is attached, send multipart/form-data
      if (receiptFile || manualPaymentComment) {
        const form = new FormData();
        form.append("leadId", leadMongoId);
        form.append("transactionID", transactionID);
        form.append("amount", Number(totalAmount));
        // Indicate manual override so server skips payment-link verification
        form.append("manualOverride", true);
        if (receiptFile) form.append("receipt", receiptFile);
        if (manualPaymentComment) form.append("comment", manualPaymentComment);

        res = await axios.patch(
          `${import.meta.env.VITE_API_URL}/management/payment-paid/${paymentId}`,
          form,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
      } else {
        res = await axios.patch(
          `${import.meta.env.VITE_API_URL}/management/payment-paid/${paymentId}`,
          {
            leadId: leadMongoId,
            transactionID,
            amount: Number(totalAmount),
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }

      // console.log("Payment marked paid response:", res.data);

      // Extract booking information from response
      const bookingID = res.data?.data?.booking?.bookingID || null;

      toast({
        title: "Payment Marked Paid",
        description: "Payment successfully updated.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setPaymentStatus("PAID");

      // clear receipt and comment after success
      setReceiptFile(null);
      setReceiptPreview(null);
      setManualPaymentComment("");

      navigate("/bookings/lead/booking-confirmed", {
        state: {
          leadId,
          bookingId: bookingID, // Pass the actual booking ID
          amount: totalAmount,
          systemCalculated,
          tokenAmount,
          bookingData,
          customerData,
          transactionID,
        },
      });
    } catch (error) {
      console.error("Payment Error:", error);

      // Better error handling to avoid null reference
      let errorMessage = "An error occurred while marking payment as paid";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        title: "Error Marking Payment",
        description: errorMessage,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const statusColor = paymentStatus === "PAID" ? "green" : "orange";

  return (
    <Box bg="#F3F6FD" minH="100vh" p={{ base: 4, md: 8 }}>
      <Box mb={4}>
        <BreadCrumbMaker
          items={[
            { title: "Dashboard", link: "/" },
            { title: "Bookings", link: "/bookings" },
            // { title: "Add Leads", link: "/bookings/lead/results" },
            { title: "Booking Status", link: "/bookings/lead/booking-status" },
          ]}
        />
      </Box>

      <Box w={{ base: "100%", lg: "85%" }} mx="auto">
        {/* Booking Details */}
        <Box
          bg="white"
          p={6}
          rounded="md"
          border="1px solid #E8EDF7"
          boxShadow="sm"
          mb={6}
        >
          <Heading size="md" mb={5} color="#2C3653">
            Booking Details
          </Heading>

          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6}>
            <Box>
              <Text fontSize="sm" color="#6B7280">
                Booking ID
              </Text>
              <Text fontWeight="600">{leadId}</Text>
            </Box>
            <Box>
              <Text fontSize="sm" color="#6B7280">
                Booking Date
              </Text>
              <Text fontWeight="600">{bookingData.pickupdate}</Text>
            </Box>
            <Box>
              <Text fontSize="sm" color="#6B7280">
                Booking Time
              </Text>
              <Text fontWeight="600">
                {formatTime12(bookingData.pickuptime)}
              </Text>
            </Box>
            <Box>
              <Text fontSize="sm" color="#6B7280">
                Car Model
              </Text>
              <Text fontWeight="600">
                {bookingData.carDetails?.name || "Car Segment"}
              </Text>
            </Box>
          </SimpleGrid>

          <Divider my={4} />

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <Box>
              <Text fontSize="sm" color="#6B7280">
                Pickup Location
              </Text>
              <Text fontWeight="600">{bookingData.from}</Text>
            </Box>
            {shouldShowDropLocation && (
              <Box>
                <Text fontSize="sm" color="#6B7280">
                  Drop Location
                </Text>
                <Text fontWeight="600">{bookingData.to}</Text>
              </Box>
            )}
            <Box>
              <Flex align="center" justify="space-between">
                <Box>
                  <Text fontSize="sm" color="#6B7280">
                    Original Price
                  </Text>
                  <Text fontWeight="600">₹ {amount.toFixed(2)}</Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="#6B7280">
                    Discount
                  </Text>
                  <Text fontWeight="600">
                    ₹ {(amount - totalAmount).toFixed(2)}
                  </Text>
                </Box>
                {(amount - totalAmount).toFixed(2) > 0 && (
                  <Box>
                    <Text fontSize="sm" color="#6B7280">
                      Final Price
                    </Text>
                    <Text fontWeight="600">₹ {totalAmount.toFixed(2)}</Text>
                  </Box>
                )}
                <Box textAlign="right">
                  <Text fontSize="sm" color="#6B7280">
                    Token Amount
                  </Text>
                  <Text fontWeight="600">₹ {Math.round(tokenAmount)}</Text>
                </Box>
              </Flex>
            </Box>
          </SimpleGrid>
        </Box>

        {/* Customer Details */}
        <Box
          bg="white"
          p={6}
          rounded="md"
          border="1px solid #E8EDF7"
          boxShadow="sm"
          mb={6}
        >
          <Heading size="md" mb={5} color="#2C3653">
            Customer Details
          </Heading>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            <Box>
              <Text fontSize="sm" color="#6B7280">
                Customer Name
              </Text>
              <Text fontWeight="600">{customerData.name}</Text>
            </Box>
            <Box>
              <Text fontSize="sm" color="#6B7280">
                Mobile Number
              </Text>
              <Text fontWeight="600">{customerData.mobile}</Text>
            </Box>
            <Box>
              <Text fontSize="sm" color="#6B7280">
                Email ID
              </Text>
              <Text fontWeight="600">{customerData.email || "N/A"}</Text>
            </Box>
            <Box>
              {passengers && passengers.length > 0 && (
                <Box>
                  <Text fontSize="sm" color="#6B7280">
                    Passengers
                  </Text>
                  <Text fontWeight="600">
                    {passengers.map((p, i) => p.name || "N/A").join(", ")}
                  </Text>
                </Box>
              )}
            </Box>
          </SimpleGrid>

          {bookingData.comments && (
            <Box mt={5}>
              <Text fontSize="sm" color="#6B7280">
                Notes
              </Text>
              <Text fontWeight="600">{bookingData.comments}</Text>
            </Box>
          )}
        </Box>

        {/* Token Payment Status */}
        <Box
          bg="white"
          p={6}
          rounded="md"
          border="1px solid #E8EDF7"
          boxShadow="sm"
        >
          <Heading size="md" mb={5} color="#2C3653">
            Token Payment Status
          </Heading>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <Box>
              <Text fontSize="sm" color="#6B7280">
                Status
              </Text>
              <Badge
                mt={1}
                px={3}
                py={1}
                colorScheme={statusColor}
                rounded="md"
              >
                {paymentStatus}
              </Badge>
            </Box>
            <Box>
              <Text fontSize="sm" color="#6B7280">
                Token Reference
              </Text>
              <Text fontWeight="600">{`TOKEN-${leadId?.split('-').pop() || ''}`}</Text>
            </Box>
          </SimpleGrid>

          <Text mt={4} color="#555" fontSize="sm">
            {paymentStatus === "PENDING"
              ? "Waiting for customer to complete the payment using the token."
              : "Payment has been successfully completed."}
          </Text>

          {/* Manual Payment Section */}
          <Box mt={4}>
            <Text fontWeight="600" mb={2} fontSize="sm">
              Manual Payment Confirmation
            </Text>

            {/* Receipt Upload */}
            <Box mb={3}>
              <Text fontSize="sm" color="gray.600" mb={1}>
                Upload Payment Receipt (Optional)
              </Text>
              <input
                id="receiptFile"
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.target.files[0];
                  if (f) {
                    setReceiptFile(f);
                    setReceiptPreview(URL.createObjectURL(f));
                  } else {
                    setReceiptFile(null);
                    setReceiptPreview(null);
                  }
                }}
              />
              <label htmlFor="receiptFile">
                <Button size="sm" as="span" variant="outline">
                  {receiptFile ? "Change Receipt" : "Upload Receipt"}
                </Button>
              </label>
              {receiptPreview && (
                <Box mt={2}>
                  <img
                    src={receiptPreview}
                    alt="receipt"
                    style={{
                      maxWidth: 240,
                      maxHeight: 160,
                      borderRadius: "4px",
                    }}
                  />
                  <Button
                    size="xs"
                    mt={1}
                    colorScheme="red"
                    variant="ghost"
                    onClick={() => {
                      setReceiptFile(null);
                      setReceiptPreview(null);
                    }}
                  >
                    Remove
                  </Button>
                </Box>
              )}
            </Box>

            {/* Payment Comment */}
            <Box>
              <Text fontSize="sm" color="gray.600" mb={1}>
                Payment Notes/Comments
              </Text>
              <Textarea
                placeholder="Enter payment details, transaction ID, or any notes..."
                value={manualPaymentComment}
                onChange={(e) => setManualPaymentComment(e.target.value)}
                size="sm"
                resize="vertical"
                minH="80px"
              />
            </Box>
          </Box>

          <Button
            mt={5}
            colorScheme="blue"
            w="140px"
            onClick={handleMarkAsPaid}
            isLoading={loading}
            isDisabled={paymentStatus === "PAID"}
          >
            Mark as Paid
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
